/**
 * UI Reducer for Data Upload Page
 * Manages all UI state with typed actions
 */

import { 
  UploadUIState, 
  UploadUIAction, 
  DEFAULT_COLUMN_VALIDATION 
} from '../types/dataUpload.types';

/**
 * Initial UI state
 */
export const initialUIState: UploadUIState = {
  currentStep: 1,
  completedSteps: [],
  isLoading: false,
  loadingMessage: null,
  isNameModalOpen: false,
  saveDataName: '',
  errorMessage: null,
  errorCode: null,
  errorSuggestion: null,
  errorPersistent: false,
  successMessage: null,
  showFileUpload: true,
  showActionButtons: false,
  showDelimiterInput: false,
  shouldScrollToBottom: false,
  columnValidationErrors: DEFAULT_COLUMN_VALIDATION,
  showUpgradeModal: false,
  upgradeMessage: '',
  upgradeResourceType: 'upload'
};

/**
 * UI Reducer function
 */
export const uploadUIReducer = (
  state: UploadUIState, 
  action: UploadUIAction
): UploadUIState => {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
      
    case 'MARK_STEP_COMPLETED':
      return {
        ...state,
        completedSteps: state.completedSteps.includes(action.payload)
          ? state.completedSteps
          : [...state.completedSteps, action.payload]
      };
      
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_LOADING_MESSAGE':
      return { ...state, loadingMessage: action.payload };

    case 'TOGGLE_MODAL':
      return { ...state, isNameModalOpen: action.payload };
      
    case 'SET_SAVE_NAME':
      return { ...state, saveDataName: action.payload };
      
    case 'SET_ERROR': {
      const payload = action.payload;
      if (payload === null || typeof payload === 'string') {
        return {
          ...state,
          errorMessage: payload,
          errorCode: null,
          errorSuggestion: null,
          errorPersistent: false,
          successMessage: null,
        };
      }
      return {
        ...state,
        errorMessage: payload.message,
        errorCode: payload.code ?? null,
        errorSuggestion: payload.suggestion ?? null,
        errorPersistent: payload.persistent === true,
        successMessage: null,
      };
    }
      
    case 'SET_SUCCESS':
      return {
        ...state,
        successMessage: action.payload,
        errorMessage: null,
        errorCode: null,
        errorSuggestion: null,
        errorPersistent: false,
      };
      
    case 'SHOW_FILE_UPLOAD':
      return { ...state, showFileUpload: action.payload };
      
    case 'SHOW_ACTION_BUTTONS':
      return { ...state, showActionButtons: action.payload };
      
    case 'SHOW_DELIMITER_INPUT':
      return { ...state, showDelimiterInput: action.payload };
      
    case 'SET_SCROLL_BOTTOM':
      return { ...state, shouldScrollToBottom: action.payload };
      
    case 'SET_COLUMN_ERRORS':
      return { ...state, columnValidationErrors: action.payload };
      
    case 'CLEAR_COLUMN_ERRORS':
      return { ...state, columnValidationErrors: DEFAULT_COLUMN_VALIDATION };
      
    case 'SHOW_UPGRADE_MODAL':
      return { ...state, showUpgradeModal: action.payload };
      
    case 'SET_UPGRADE_INFO':
      return { 
        ...state, 
        upgradeMessage: action.payload.message,
        upgradeResourceType: action.payload.resourceType,
        showUpgradeModal: true
      };
      
    case 'START_PROCESSING':
      return { 
        ...state, 
        isLoading: true,
        showFileUpload: false
      };
      
    case 'STOP_PROCESSING':
      return { 
        ...state, 
        isLoading: false
      };
      
    case 'RESET_UI':
      return initialUIState;
      
    default:
      return state;
  }
};
